import { Node, NodeProps } from '@xyflow/react';

/** Fields consumed by scope, properties, and execution calculations; coordinates are deliberately excluded. */
export function sameGraphStructure(left: Node[], right: Node[]): boolean {
	return left === right || (left.length === right.length && left.every((node, index) => {
		const other = right[index];
		return node === other || (node.id === other.id && node.data === other.data && node.type === other.type
			&& node.parentId === other.parentId && node.hidden === other.hidden);
	}));
}

export function sameGraphSelection(left: Node[], right: Node[]): boolean {
	return sameGraphStructure(left, right) && left.every((node, index) => node.selected === right[index].selected);
}

/** Only compare props actually used by the custom node content. React Flow still moves its outer wrapper. */
export function sameNodeContent(left: NodeProps, right: NodeProps): boolean {
	return left.id === right.id && left.data === right.data && left.selected === right.selected
		&& left.width === right.width && left.height === right.height;
}

export function createStableGraphNodes<T extends Node>() {
	let previous: T[] | undefined;
	return (nodes: T[]): T[] => {
		if (!previous || !sameGraphStructure(previous, nodes)) { previous = nodes; }
		return previous;
	};
}

/** Keep dirty-check serialization off the drag path; save/copy use live nodes separately. */
export function createCommittedGraphNodes<T extends Node>() {
	let previous: T[] | undefined;
	return (nodes: T[]): T[] => {
		if (previous && nodes.some((node) => node.dragging)) { return previous; }
		if (!previous || !sameGraphStructure(previous, nodes) || nodes.some((node, index) => {
			const old = previous![index];
			return old.position.x !== node.position.x || old.position.y !== node.position.y
				|| old.width !== node.width || old.height !== node.height;
		})) { previous = nodes; }
		return previous;
	};
}
